/**
 * HandsFreeProvider
 *
 * App-level (above the router) controller for the hands-free voice co-pilot.
 * Living above the routes is what fixes the cross-page breakage: it owns ONE
 * AudioContext / WebAudioManager that is created+resumed on a user gesture (the
 * toggle click) and kept alive across navigation — so audio never lands in the
 * "suspended" state after a programmatic, voice-driven page change.
 *
 * Responsibilities:
 *  - Reading: dedicated TTS (POST /api/game/tts, streamed PCM16 @ 24kHz) fed to
 *    the existing 24kHz playback pipeline. Fast + cheap (no Realtime output).
 *  - Listening: a Realtime session with output_modalities:['text'] (no audio
 *    output → cheap) that only maps speech to navigation tool calls.
 *  - Read-then-listen sequencing, auto-stop after silence, mic handoff to the
 *    conversation (releases mic when no nav screen is registered).
 *  - A small fixed status pill (reading / listening / tap-to-talk / tap-to-enable).
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { WebAudioManager, WebSocketService, REALTIME_MODEL, type ToolDefinition } from '@selna/session';
import { getHandsFree, setHandsFree } from '../utils/voiceNavPreference';
import { navApiBaseUrl, navAuthHeaders } from './navAuth';
import { NAV_LISTEN_INSTRUCTIONS } from './navPrompts';
import { NAV_TOOL } from './navTools';
import { createTtsPlayer, type TtsPlayer } from './ttsPlayer';

export interface HandsFreeScreen {
  /** Auth token (magic link) for the TTS + realtime-token calls. */
  token: string;
  /** Identity of the current read content. Re-reads only when this changes. */
  readKey: string;
  /** Plain text to speak (TTS). */
  readText: string;
  /**
   * If set and it equals the provider's last-read key, this registration is a
   * CONTINUATION of that read: the provider keeps it playing (or treats it as
   * already read) and speaks only `readTail` instead of restarting `readText`.
   * Used so "next episode ready" doesn't restart a recap that's already playing.
   */
  continuesReadKey?: string;
  /** Short text appended (and spoken on its own) when `continuesReadKey` matches. */
  readTail?: string;
  /** Commands available on this screen. */
  tools: ToolDefinition[];
  /** Dispatch a recognised command (read_again is handled internally). */
  onCommand: (name: string, args: Record<string, unknown>) => void;
}

interface HandsFreeContextValue {
  enabled: boolean;
  /** Call from a user gesture (click) — needed to unlock the AudioContext. */
  setEnabled: (next: boolean) => void;
  registerScreen: (screen: HandsFreeScreen | null) => void;
  isReading: boolean;
  isListening: boolean;
}

const HandsFreeContext = createContext<HandsFreeContextValue | null>(null);

export function useHandsFree(): HandsFreeContextValue {
  const ctx = useContext(HandsFreeContext);
  if (!ctx) throw new Error('useHandsFree must be used within <HandsFreeProvider>');
  return ctx;
}

const SEMANTIC_VAD = {
  type: 'realtime' as const,
  audio: { input: { turn_detection: { type: 'semantic_vad' as const, eagerness: 'high' as const } } },
};
const TURN_OFF = {
  type: 'realtime' as const,
  audio: { input: { turn_detection: null } },
};

const SILENCE_TIMEOUT_MS = 25_000;

export function HandsFreeProvider({ children }: { children: ReactNode }) {
  const [enabled, setEnabledState] = useState<boolean>(getHandsFree);
  const [isReading, setIsReading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [idleTimedOut, setIdleTimedOut] = useState(false);
  const [audioReady, setAudioReady] = useState(false);

  const audioRef = useRef<WebAudioManager | null>(null);
  const audioReadyRef = useRef(false);
  const wsRef = useRef<WebSocketService | null>(null);
  const screenRef = useRef<HandsFreeScreen | null>(null);
  const lastReadKeyRef = useRef<string | null>(null);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ttsAbortRef = useRef<AbortController | null>(null);
  const isReadingRef = useRef(false);
  // A short follow-up to speak once the current read ends (used to append the
  // "next episode ready" prompt without restarting an in-progress recap).
  const pendingTailRef = useRef<string | null>(null);
  // Teardown of an in-progress read is deferred (microtask) so a continuation
  // screen registering right after can cancel it — see registerScreen().
  const pendingTeardownRef = useRef(false);
  const ttsPlayerRef = useRef<TtsPlayer | null>(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  // Dedicated <audio>-based TTS player (native MP3 decode, streamed via MSE).
  // Guard against SSR/prerender (no Audio in Node).
  if (!ttsPlayerRef.current && typeof window !== 'undefined' && typeof Audio !== 'undefined') {
    ttsPlayerRef.current = createTtsPlayer(navApiBaseUrl(), (token) => navAuthHeaders(token)());
  }

  // ── Audio init (must run within a user gesture) ───────────────────────────
  const ensureAudio = useCallback(async () => {
    if (audioReadyRef.current && audioRef.current) return;
    const audio = new WebAudioManager();
    audioRef.current = audio;
    await audio.initializeAudio(); // creates + resumes the AudioContext
    audioReadyRef.current = true;
    setAudioReady(true);
  }, []);

  // ── Listening control ──────────────────────────────────────────────────────
  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const stopListening = useCallback(() => {
    clearSilenceTimer();
    const audio = audioRef.current;
    const ws = wsRef.current;
    audio?.pauseMicrophone();
    if (ws && ws.isConnected()) {
      void ws.updateSessionConfig(TURN_OFF);
    }
    setIsListening(false);
  }, [clearSilenceTimer]);

  const armListening = useCallback(async () => {
    const audio = audioRef.current;
    const ws = wsRef.current;
    if (!audio || !ws || !ws.isConnected()) return;
    try {
      await audio.ensureMicrophoneReady();
      audio.setPTTMode?.(false);
      ws.clearAudioBuffer();
      if (!audio.isRecordingActive()) audio.startRecording();
      else audio.resumeMicrophone();
      await ws.updateSessionConfig(SEMANTIC_VAD);
      setIsListening(true);
      setIdleTimedOut(false);
      clearSilenceTimer();
      silenceTimerRef.current = setTimeout(() => {
        stopListening();
        setIdleTimedOut(true);
      }, SILENCE_TIMEOUT_MS);
    } catch (err) {
      console.warn('[🧭 HandsFree] arm listening failed', err);
    }
  }, [clearSilenceTimer, stopListening]);

  const speakRef = useRef<(text: string) => Promise<void>>(async () => {});

  const handleFunctionCall = useCallback(
    (callId: string, name: string, args: Record<string, unknown>) => {
      wsRef.current?.sendFunctionResult(callId, 'OK', false);
      if (name === NAV_TOOL.READ_AGAIN) {
        const s = screenRef.current;
        if (s) void speakRef.current(s.readText);
        return;
      }
      stopListening();
      screenRef.current?.onCommand(name, args || {});
    },
    [stopListening],
  );

  const disconnectListening = useCallback(() => {
    clearSilenceTimer();
    try {
      wsRef.current?.disconnect();
    } catch {
      /* ignore */
    }
    wsRef.current = null;
    audioRef.current?.stopRecording();
    setIsListening(false);
  }, [clearSilenceTimer]);

  const ensureListening = useCallback(
    async (token: string, tools: ToolDefinition[]) => {
      const audio = audioRef.current;
      if (!audio) return;
      const existing = wsRef.current;
      if (existing && existing.isConnected()) {
        await existing.updateSessionConfig({ type: 'realtime', tools, tool_choice: 'auto' });
        return;
      }
      // Mint a realtime token (listening only — output is text).
      const resp = await fetch(`${navApiBaseUrl()}/realtime-ws/token`, {
        method: 'POST',
        headers: navAuthHeaders(token)(),
        body: JSON.stringify({ model: REALTIME_MODEL }),
      });
      if (!resp.ok) {
        console.warn('[🧭 HandsFree] realtime token failed', resp.status);
        return;
      }
      const { token: ephemeral } = await resp.json();
      const ws = new WebSocketService({
        onFunctionCall: handleFunctionCall,
        onUserSpeechStarted: () => clearSilenceTimer(),
        onError: (e) => console.warn('[🧭 HandsFree] ws error', e),
      });
      wsRef.current = ws;
      await ws.connect(ephemeral, NAV_LISTEN_INSTRUCTIONS, { model: REALTIME_MODEL });
      audio.on('onAudioData', (pcm16: Uint8Array) => wsRef.current?.sendAudio(pcm16));
      // Text-only output (no expensive audio), tools, turn detection off until armed.
      await ws.updateSessionConfig({
        type: 'realtime',
        output_modalities: ['text'],
        tools,
        tool_choice: 'auto',
        audio: { input: { turn_detection: null } },
      });
      // NOTE: we deliberately do NOT acquire the microphone here. Opening it
      // during reading turns on the mic indicator, switches the device into
      // "call" mode (quieter), and — since the TTS <audio> is outside the AEC
      // graph — lets the read's own audio echo into the mic and confuse the VAD.
      // The mic is acquired only in armListening(), after reading has finished.
    },
    [handleFunctionCall, clearSilenceTimer],
  );

  // ── Reading (TTS) ────────────────────────────────────────────────────────
  // Reading goes through the dedicated <audio>/MSE player (native MP3 decode):
  // progressive (first audio in ~1s) and gapless (no manual-PCM clicks). The
  // promise resolves when playback actually ENDS, which is the right moment to
  // start listening.
  const speak = useCallback(
    async (text: string) => {
      const player = ttsPlayerRef.current;
      const screen = screenRef.current;
      if (!player || !screen || !text.trim()) return;
      stopListening();
      ttsAbortRef.current?.abort();
      const ac = new AbortController();
      ttsAbortRef.current = ac;
      setIsReading(true);
      isReadingRef.current = true;
      try {
        await player.speak(text, screen.token, ac.signal);
      } catch (err) {
        if (!ac.signal.aborted) console.warn('[🧭 HandsFree] TTS error', err);
      } finally {
        if (ttsAbortRef.current === ac) {
          if (ac.signal.aborted) {
            setIsReading(false);
            isReadingRef.current = false;
          } else {
            // A tail queued mid-read (e.g. "episode ready") plays seamlessly
            // next, keeping the pill on "A ler…"; otherwise hand off to the mic.
            const tail = pendingTailRef.current;
            pendingTailRef.current = null;
            if (tail && tail.trim()) {
              void speakRef.current(tail);
            } else {
              setIsReading(false);
              isReadingRef.current = false;
              void armListening();
            }
          }
        }
      }
    },
    [stopListening, armListening],
  );
  speakRef.current = speak;

  // ── Screen driving ────────────────────────────────────────────────────────
  const driveScreen = useCallback(
    async (screen: HandsFreeScreen) => {
      if (!enabledRef.current || !audioReadyRef.current) return;
      // Continuation: the read this screen builds on (e.g. the recap) is already
      // playing or done. Don't restart it — just make the new commands available
      // and speak only the short tail (e.g. "o próximo episódio está pronto").
      if (screen.continuesReadKey && lastReadKeyRef.current === screen.continuesReadKey) {
        lastReadKeyRef.current = screen.readKey;
        await ensureListening(screen.token, screen.tools);
        const tail = screen.readTail?.trim() ? screen.readTail : '';
        if (isReadingRef.current) {
          // Recap still playing → queue the tail to speak when it ends.
          pendingTailRef.current = tail || null;
        } else if (tail) {
          // Recap already finished → speak just the tail now.
          await speak(tail);
        }
        return;
      }
      if (lastReadKeyRef.current === screen.readKey) return;
      lastReadKeyRef.current = screen.readKey;
      await ensureListening(screen.token, screen.tools);
      await speak(screen.readText);
    },
    [ensureListening, speak],
  );

  // Stop any in-progress read and pause the mic. Pulled out of registerScreen so
  // it can be deferred and cancelled by a continuation (see below).
  const teardownRead = useCallback(() => {
    pendingTailRef.current = null;
    ttsAbortRef.current?.abort();
    ttsPlayerRef.current?.stop(); // cut any in-progress read
    setIsReading(false);
    isReadingRef.current = false;
    stopListening();
  }, [stopListening]);

  const registerScreen = useCallback(
    (screen: HandsFreeScreen | null) => {
      screenRef.current = screen;
      if (!screen) {
        // Leaving a nav screen (e.g. entering the conversation, or a route change):
        // stop reading and pause the mic so the conversation's own session can
        // capture audio. Keep the AudioContext AND the (idle) realtime session
        // alive so the next nav screen doesn't have to mint a new token.
        //
        // We DEFER the teardown to a microtask instead of stopping the read now.
        // A phase transition within the same page (e.g. recap -> ready) runs the
        // old effect's cleanup — this registerScreen(null) — synchronously right
        // before the new screen registers. If that new screen continues the read
        // currently playing, it cancels this teardown (below) so the recap keeps
        // playing and the "next episode ready" tail merely queues instead of
        // cutting it off. When nothing follows (a real exit), the microtask fires
        // and the read stops. (We also keep lastReadKeyRef so the continuesReadKey
        // check in driveScreen() still matches.)
        pendingTeardownRef.current = true;
        queueMicrotask(() => {
          if (pendingTeardownRef.current) {
            pendingTeardownRef.current = false;
            teardownRead();
          }
        });
        return;
      }
      // A new screen registered. If a teardown is pending and this screen does
      // NOT continue the read in progress, run it now (stop the old read) before
      // driving the new one. If it IS a continuation, cancel the teardown so the
      // current read survives and driveScreen() can queue the tail.
      if (pendingTeardownRef.current) {
        const continuesCurrentRead =
          !!screen.continuesReadKey && lastReadKeyRef.current === screen.continuesReadKey;
        pendingTeardownRef.current = false;
        if (!continuesCurrentRead) teardownRead();
      }
      if (enabledRef.current && audioReadyRef.current) {
        void driveScreen(screen);
      }
    },
    [driveScreen, teardownRead],
  );

  // ── Enable / disable ────────────────────────────────────────────────────────
  const setEnabled = useCallback(
    (next: boolean) => {
      setHandsFree(next);
      setEnabledState(next);
      if (next) {
        // Runs within the toggle click → unlock the AudioContext + <audio> now.
        ttsPlayerRef.current?.unlock();
        void (async () => {
          await ensureAudio();
          if (screenRef.current) void driveScreen(screenRef.current);
        })();
      } else {
        ttsAbortRef.current?.abort();
        ttsPlayerRef.current?.stop();
        setIsReading(false);
        isReadingRef.current = false;
        pendingTailRef.current = null;
        setIdleTimedOut(false);
        disconnectListening();
        lastReadKeyRef.current = null;
        void audioRef.current?.cleanup();
        audioRef.current = null;
        audioReadyRef.current = false;
        setAudioReady(false);
      }
    },
    [ensureAudio, driveScreen, disconnectListening],
  );

  // If the toggle was left ON across a reload, unlock audio on the first gesture.
  useEffect(() => {
    if (!enabled || audioReadyRef.current) return;
    const unlock = async () => {
      ttsPlayerRef.current?.unlock();
      await ensureAudio();
      if (screenRef.current) void driveScreen(screenRef.current);
    };
    const onGesture = () => void unlock();
    window.addEventListener('pointerdown', onGesture, { once: true });
    window.addEventListener('keydown', onGesture, { once: true });
    return () => {
      window.removeEventListener('pointerdown', onGesture);
      window.removeEventListener('keydown', onGesture);
    };
  }, [enabled, ensureAudio, driveScreen]);

  // Tap-to-talk after a silence timeout (also a gesture → safe to re-arm).
  const relisten = useCallback(async () => {
    setIdleTimedOut(false);
    await ensureAudio();
    void armListening();
  }, [ensureAudio, armListening]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      ttsAbortRef.current?.abort();
      clearSilenceTimer();
      ttsPlayerRef.current?.dispose();
      try {
        wsRef.current?.disconnect();
      } catch {
        /* ignore */
      }
      void audioRef.current?.cleanup();
    };
  }, [clearSilenceTimer]);

  return (
    <HandsFreeContext.Provider value={{ enabled, setEnabled, registerScreen, isReading, isListening }}>
      {children}
      {enabled && (
        <HandsFreeStatusPill
          audioReady={audioReady}
          isReading={isReading}
          isListening={isListening}
          idleTimedOut={idleTimedOut}
          onRelisten={relisten}
        />
      )}
    </HandsFreeContext.Provider>
  );
}

function HandsFreeStatusPill({
  audioReady,
  isReading,
  isListening,
  idleTimedOut,
  onRelisten,
}: {
  audioReady: boolean;
  isReading: boolean;
  isListening: boolean;
  idleTimedOut: boolean;
  onRelisten: () => void;
}) {
  let label = '';
  let icon = '';
  if (!audioReady) {
    label = 'Toca no ecrã para ativar a voz';
    icon = '👆';
  } else if (isReading) {
    label = 'A ler…';
    icon = '🔊';
  } else if (isListening) {
    label = 'À escuta…';
    icon = '🎤';
  } else if (idleTimedOut) {
    label = 'Toca para falar';
    icon = '🎤';
  }
  if (!label) return null;

  const content = (
    <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-dark/90 text-white text-sm font-medium shadow-lg backdrop-blur">
      <span aria-hidden className={isListening ? 'animate-pulse' : ''}>{icon}</span>
      <span>{label}</span>
    </div>
  );

  return (
    <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50">
      {idleTimedOut ? (
        <button type="button" onClick={onRelisten}>
          {content}
        </button>
      ) : (
        content
      )}
    </div>
  );
}
